import * as THREE from 'three/webgpu';
import { attribute } from 'three/tsl';

import { blobShadowMaterial, RENDER_ORDER } from './materials.js';

/**
 * @file `ShadowBlobPool` — the third leg of the no-shadow-map plan.
 *
 * Static geometry gets baked sun visibility and projected hard-shadow decals;
 * DYNAMIC actors get one of these: a soft radial smear on the ground that says
 * "this thing is standing here, and it is standing HERE, not floating". Without
 * it a billboard sprite reads as a sticker on the camera. It is the cheapest,
 * highest-value shadow in the whole project — one instanced quad per actor,
 * one draw call for all of them.
 *
 * Pooled with a free list because actors come and go (spawner, despawner) and an
 * InstancedMesh cannot cheaply compact: `acquire()` hands out a stable slot,
 * `release()` returns it (and zeroes the transform so the slot draws nothing).
 *
 * DEVIATIONS worth knowing:
 * - The falloff texture is a `DataTexture` built arithmetically, not a canvas
 *   `createRadialGradient`. Same image, minus the DOM dependency — this class
 *   then constructs fine in node/worker contexts and in unit tests.
 * - Per-instance alpha rides an `iAlpha` instanced attribute multiplied INTO the
 *   material's `opacityNode`. `blobShadowMaterial` (T2, not ours to edit) has one
 *   global opacity; a fading/shrinking actor needs its own.
 * - `frustumCulled = false`: the blobs move every frame, so a correct bounding
 *   sphere would have to be recomputed every frame to save one cheap draw call.
 *
 * @module engine/render/ShadowBlobs
 */

export class ShadowBlobPool {
  /**
   * @param {Object} [o]
   * @param {number} [o.max=600] Slot cap.
   * @param {number} [o.opacity=0.5] Peak darkness at noon.
   * @param {number} [o.nightFade=0.6] Blobs keep some presence under the moon.
   * @param {number} [o.lift=0.02] Metres above the sampled ground. Enough to
   *   clear z-fighting on a heightfield, small enough that it never separates.
   * @param {number} [o.size=64] Falloff texture resolution.
   * @param {number} [o.softness=0.45] 0..1 — where the falloff starts. Lower is
   *   a harder-edged puddle, higher is a diffuse smudge.
   * @param {import('../types.js').Registry|null} [o.registry] If given, the
   *   texture + material are registered so `Level.dispose()` frees them.
   * @param {string} [o.name='shadowBlobs']
   */
  constructor(o = {}) {
    const {
      max = 600,
      opacity = 0.5,
      nightFade = 0.6,
      lift = 0.02,
      size = 64,
      softness = 0.45,
      registry = null,
      name = 'shadowBlobs',
    } = o;

    if (!(max > 0)) throw new Error('ShadowBlobPool: `max` must be > 0');

    this.max = max | 0;
    this.lift = lift;
    /** @type {number} High-water mark — how many slots the draw covers. */
    this.count = 0;
    /** @type {number[]} Recycled slots. */
    this._free = [];
    /** @type {Uint8Array} 1 = live. Guards double-release. */
    this._live = new Uint8Array(this.max);
    this._disposed = false;
    this._overflow = 0;

    /** @type {THREE.Texture} */
    this.texture = registry
      ? registry.texture(`${name}:falloff`, () => makeFalloffTexture(size, softness))
      : makeFalloffTexture(size, softness);

    // A unit quad lying in the XZ plane, normal +Y.
    const geo = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
    this._alphaAttr = new THREE.InstancedBufferAttribute(new Float32Array(this.max), 1);
    geo.setAttribute('iAlpha', this._alphaAttr);
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e5);

    const mat = blobShadowMaterial({
      tex: this.texture,
      opacity,
      nightFade,
      name,
    });
    // Per-instance fade on top of the material's global curve.
    mat.opacityNode = mat.opacityNode.mul(attribute('iAlpha', 'float'));
    if (registry) registry.track?.(mat);

    /** @type {THREE.Material} */
    this.material = mat;

    /** @type {THREE.InstancedMesh} */
    this.object = new THREE.InstancedMesh(geo, mat, this.max);
    this.object.name = name;
    this.object.count = 0;
    this.object.renderOrder = RENDER_ORDER.blobShadow;
    this.object.frustumCulled = false;
    this.object.matrixAutoUpdate = false;
    this.object.updateMatrix();
    // Untouched slots must draw nothing, and `InstancedMesh` fills its matrices
    // with identity (a 1x1 quad at the origin), not zero.
    this.object.instanceMatrix.array.fill(0);

    this._m = new THREE.Matrix4();
  }

  /**
   * @returns {number} A stable slot id, or `-1` when the pool is exhausted
   *   (logged once — an exhausted pool means the actor budget was set too low).
   */
  acquire() {
    let id;
    if (this._free.length) {
      id = this._free.pop();
    } else if (this.count < this.max) {
      id = this.count++;
    } else {
      if (this._overflow++ === 0) {
        console.warn(`[ShadowBlobPool] exhausted at ${this.max} blobs; extra acquires return -1`);
      }
      return -1;
    }
    this._live[id] = 1;
    this._alphaAttr.array[id] = 0;
    return id;
  }

  /**
   * @param {number} id From {@link acquire}.
   * @param {number} x @param {number} y Ground height at (x,z) — `lift` is added
   *   for you. @param {number} z
   * @param {number} r Blob RADIUS in metres (the quad is `2r` across).
   * @param {number} [alpha=1] 0..1 on top of the pool's global opacity. Fade it
   *   with height for jumps, with distance for despawns.
   */
  set(id, x, y, z, r, alpha = 1) {
    if (id < 0 || id >= this.max || !this._live[id]) return;
    const d = r * 2;
    this._m.makeScale(d, 1, d);
    this._m.elements[12] = x;
    this._m.elements[13] = y + this.lift;
    this._m.elements[14] = z;
    this.object.setMatrixAt(id, this._m);
    this._alphaAttr.array[id] = alpha < 0 ? 0 : alpha > 1 ? 1 : alpha;
  }

  /**
   * Returns a slot to the pool and hides it.
   * @param {number} id
   */
  release(id) {
    if (id < 0 || id >= this.max || !this._live[id]) return;
    this._live[id] = 0;
    this._alphaAttr.array[id] = 0;
    // Zero scale AND zero translation: a degenerate quad rasterises nothing.
    const e = this.object.instanceMatrix.array;
    const o = id * 16;
    for (let k = 0; k < 16; k++) e[o + k] = 0;
    this._free.push(id);
  }

  /** Uploads the frame's transforms + alphas. Call once per frame. */
  commit() {
    if (this._disposed) return;
    this.object.instanceMatrix.needsUpdate = true;
    this._alphaAttr.needsUpdate = true;
    this.object.count = this.count;
    this.object.visible = this.count > 0;
  }

  /** @returns {{count: number, max: number, free: number, dropped: number}} */
  stats() {
    return { count: this.count, max: this.max, free: this._free.length, dropped: this._overflow };
  }

  /** Idempotent. */
  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    this.object.removeFromParent();
    this.object.geometry.dispose();
    this.object.dispose();
    this.material.dispose();
    this.texture.dispose();
  }
}

/**
 * Radial falloff in the ALPHA channel (which is all `blobShadowMaterial` reads).
 * `smoothstep`-shaped rather than linear so the blob has a solid core and a soft
 * rim instead of a cone.
 *
 * @param {number} size Texture edge, pixels.
 * @param {number} softness 0..1 — normalised radius where the fade begins.
 * @returns {THREE.DataTexture}
 */
export function makeFalloffTexture(size = 64, softness = 0.45) {
  const n = Math.max(4, size | 0);
  const data = new Uint8Array(n * n * 4);
  const c = (n - 1) / 2;
  const inner = Math.min(Math.max(softness, 0), 0.95);

  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      const dx = (x - c) / (n / 2);
      const dy = (y - c) / (n / 2);
      const d = Math.hypot(dx, dy);
      // 1 inside `inner`, smoothly 0 at the texture edge.
      let a = 1 - smoothstep(inner, 1, d);
      a *= a * (3 - 2 * a); // one more ease: keeps the rim from banding
      const i = (y * n + x) * 4;
      data[i] = 0;
      data[i + 1] = 0;
      data[i + 2] = 0;
      data[i + 3] = Math.round(Math.min(1, Math.max(0, a)) * 255);
    }
  }

  const tex = new THREE.DataTexture(data, n, n, THREE.RGBAFormat);
  tex.name = 'blobFalloff';
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}

/**
 * @param {number} a @param {number} b @param {number} x
 * @returns {number} 0..1
 */
function smoothstep(a, b, x) {
  const t = b === a ? 0 : (x - a) / (b - a);
  const c = t < 0 ? 0 : t > 1 ? 1 : t;
  return c * c * (3 - 2 * c);
}
